import { useCallback } from "react";
import { format } from "date-fns";

import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import {
	showError,
	showSuccess,
	ShowSuccessFetch,
	showWarning,
} from "src/utils/toast/toastUtil";
import useUiuxStore from "src/store/useUiuxStore";

import {
	deleteTQC3010_R01,
	downloadExcel_TQC3010_R01,
	downloadReportPdf_TQC3010_R01,
	getTQC3010_R01AllCount,
	getTQC3010_R01Detail,
	getTQC3010_R01List,
	saveTQC3010_R01,
	TQC3010_R01Params,
} from "./Api_TQC3010_R01";
import {
	TQC3010_R01_COMP_CD,
	TQC3010_R01HeaderDto,
} from "./Dto_TQC3010_R01";
import {
	TQC3010_R01Filter,
	useTQC3010_R01Store,
} from "./Store_TQC3010_R01";

const toYmd = (date?: Date) =>
	date ? format(date, "yyyy-MM-dd") : undefined;

const getParams = (filter: TQC3010_R01Filter): TQC3010_R01Params => ({
	compCd: filter.compCd || undefined,
	startDate: toYmd(filter.dateRange?.from),
	endDate: toYmd(filter.dateRange?.to),
	customCd: filter.customCd || undefined,
	itemCd: filter.itemCd || undefined,
});

/**
 * 검사항목이 0건인 사유를 헤더 행이 이미 들고 있는 값으로 좁힌다.
 *
 * 검사항목은 품목코드가 아니라 `tbas_item.item_group` 단위로 등록되므로
 * (`spec.insp_item_group = item.item_group AND insp_type = 'OQC'`)
 * 조인이 어디서 끊겼느냐에 따라 사용자가 해야 할 조치가 전혀 다르다.
 * 품명·품목그룹은 Header 조회 프로젝션에 이미 실려오므로 추가 조회 없이 판별한다.
 */
const getNoDetailMessage = (row: TQC3010_R01HeaderDto) => {
	if (!row.itemNm) {
		return `품목 [${row.itemCd}] 이(가) 품목마스터에 등록되어 있지 않습니다.`;
	}
	if (!row.itemGroup) {
		return `품목 [${row.itemNm}] 에 품목그룹이 지정되지 않았습니다. 검사항목은 품목그룹 단위로 등록됩니다.`;
	}
	return `품목그룹 [${row.itemGroup}] 에 등록된 출하검사(OQC) 검사항목이 없습니다. 검사항목 등록(TQC5010)에서 먼저 등록해주세요.`;
};

export const Content_TQC3010_R01 = () => {
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { setMessage } = useUiuxStore();
	const {
		TQC3010_R01Filter,
		setTQC3010_R01PastFilter,
		TQC3010_R01Info,
		setTQC3010_R01Info,
		setTQC3010_R01List,
		setAllCount,
		selectedTQC3010_R01,
		setSelectedTQC3010_R01,
		initSelectedTQC3010_R01,
		TQC3010_R01DetailList,
		setTQC3010_R01DetailList,
		setOriginalTQC3010_R01DetailList,
		setScrollTop,
		setDetailScrollTop,
		initData,
	} = useTQC3010_R01Store();

	const fetchData = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			setMessage("");
			setScrollTop(null);
			initSelectedTQC3010_R01();
			setTQC3010_R01DetailList([]);
			setOriginalTQC3010_R01DetailList([]);
			const params = getParams(TQC3010_R01Filter);
			const response = await getTQC3010_R01List("search", params);
			const list = response.data.data ?? [];
			setTQC3010_R01List(list);
			const countResponse = await getTQC3010_R01AllCount(params);
			const allCount = countResponse.data.data ?? list.length;
			setAllCount(allCount);
			setTQC3010_R01PastFilter(TQC3010_R01Filter);
			const fetchMessage = `조회가 완료되었습니다. (총 ${list.length}/${allCount}건)`;
			if (list.length === 0) {
				setMessage(fetchMessage);
				showWarning("조회된 출하내역이 없습니다.");
			} else {
				ShowSuccessFetch(setMessage, fetchMessage);
			}
			return useTQC3010_R01Store.getState().TQC3010_R01List;
		} catch {
			showError("조회 중 오류가 발생했습니다.");
			return undefined;
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC3010_R01Filter,
		initSelectedTQC3010_R01,
		setAllCount,
		setLoadingSpinner,
		setMessage,
		setOriginalTQC3010_R01DetailList,
		setScrollTop,
		setTQC3010_R01DetailList,
		setTQC3010_R01List,
		setTQC3010_R01PastFilter,
	]);

	const handleHeaderRowClick = useCallback(
		async (row: TQC3010_R01HeaderDto) => {
			setDetailScrollTop(null);
			setSelectedTQC3010_R01(row);
			setTQC3010_R01Info({
				inspectorEmp: row.inspectorEmp ?? "",
				inspectorEmpNm: row.inspectorEmpNm ?? "",
				approvorEmpno: row.approvorEmpno ?? "",
				approvorEmpNm: row.approvorEmpNm ?? "",
			});
			try {
				setLoadingSpinner(true);
				const response = await getTQC3010_R01Detail({
					itemCd: row.itemCd,
					inspectNo: row.inspectNo || undefined,
				});

				// 주의. axios 인터셉터가 HTTP 에러를 reject 하지 않고 error.response 로 resolve 한다.
				// (axiosInterceptor.onErrorResponse) 상태코드를 직접 보지 않으면 실패 응답의
				// data(null) 가 "검사항목 0건" 으로 둔갑해 엉뚱한 안내가 나간다.
				// 실패 사유 토스트는 인터셉터가 이미 띄웠으므로 여기서 또 띄우지 않는다.
				if (response.status >= 400 || response.data?.successFlag !== true) {
					setTQC3010_R01DetailList([]);
					setOriginalTQC3010_R01DetailList([]);
					return;
				}

				const detailList = response.data.data ?? [];
				setTQC3010_R01DetailList(detailList);
				setOriginalTQC3010_R01DetailList(
					detailList.map((detail) => ({ ...detail })),
				);
				// 여기까지 왔으면 응답은 정상이고 검사항목 마스터가 실제로 비어 있는 것이다.
				if (detailList.length === 0) {
					showWarning(getNoDetailMessage(row), 3000);
				}
			} catch {
				// 인터셉터가 응답 있는 실패를 모두 resolve 로 흘려보내므로
				// 여기까지 오는 건 네트워크 단절 등 응답 자체가 없는 경우다.
				showError("검사항목을 조회하지 못했습니다. 네트워크 상태를 확인해주세요.");
			} finally {
				setLoadingSpinner(false);
			}
		},
		[
			setDetailScrollTop,
			setLoadingSpinner,
			setOriginalTQC3010_R01DetailList,
			setSelectedTQC3010_R01,
			setTQC3010_R01DetailList,
			setTQC3010_R01Info,
		],
	);

	const handleSaveClick = useCallback(async () => {
		if (!selectedTQC3010_R01) {
			showWarning("저장할 출하건을 선택해주세요.");
			return;
		}
		if (TQC3010_R01DetailList.length === 0) {
			// 행 클릭 때와 같은 사유이므로 같은 안내를 쓴다. (서버도 spec 0건이면 저장을 막는다)
			showWarning(getNoDetailMessage(selectedTQC3010_R01), 3000);
			return;
		}
		if (!TQC3010_R01Info.inspectorEmp) {
			showWarning("검사자를 입력해주세요.");
			return;
		}
		const header: TQC3010_R01HeaderDto = {
			...selectedTQC3010_R01,
			compCd: selectedTQC3010_R01.compCd || TQC3010_R01_COMP_CD,
			inspectDt:
				selectedTQC3010_R01.inspectDt || format(new Date(), "yyyy-MM-dd"),
			...TQC3010_R01Info,
		};
		try {
			setLoadingSpinner(true);
			const response = await saveTQC3010_R01({
				header,
				detailList: TQC3010_R01DetailList,
			});
			if (response.status >= 400 || response.data?.successFlag !== true) return;
			const refreshedList = await fetchData();
			const refreshedRow = refreshedList?.find(
				(row) =>
					row.transportNo === header.transportNo &&
					row.itemNo === header.itemNo,
			);
			if (refreshedRow) {
				await handleHeaderRowClick(refreshedRow);
			}
			showSuccess("저장이 완료되었습니다.");
		} catch {
			showError("저장 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC3010_R01DetailList,
		TQC3010_R01Info,
		fetchData,
		handleHeaderRowClick,
		selectedTQC3010_R01,
		setLoadingSpinner,
	]);

	const handleDeleteClick = useCallback(async () => {
		if (!selectedTQC3010_R01?.inspectNo) {
			showWarning("삭제할 검사결과를 선택해주세요.");
			return;
		}
		try {
			setLoadingSpinner(true);
			await deleteTQC3010_R01([selectedTQC3010_R01]);
			await fetchData();
			showSuccess("삭제가 완료되었습니다.");
		} catch {
			showError("삭제 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [fetchData, selectedTQC3010_R01, setLoadingSpinner]);

	const handleExcelClick = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			const response = await downloadExcel_TQC3010_R01(
				getParams(TQC3010_R01Filter),
			);
			if (!response.data.data) {
				showWarning("엑셀로 내보낼 데이터가 없습니다.");
				return;
			}
			downloadSettingExcel({
				responseData: response.data.data,
				fileName: "출하검사결과등록.xlsx",
			});
		} catch {
			showError("엑셀 다운로드 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC3010_R01Filter, setLoadingSpinner]);

	/**
	 * 출력 버튼 처리. 현재 조회조건으로 출하검사 성적서 PDF(Blob)를 받아 새 탭으로 연다.
	 * 열람 후 5초 뒤 Object URL 을 해제해 메모리 누수를 막는다.
	 *
	 * 주의. axios 인터셉터가 HTTP 에러를 reject 하지 않고 error.response 로 resolve 한다.
	 * (axiosInterceptor.onErrorResponse) 그래서 catch 만 믿으면 400/500 응답 본문을
	 * PDF 로 착각해 깨진 탭이 열린다. 상태코드와 content-type 을 직접 확인해야 한다.
	 */
	const handlePrintClick = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			const response = await downloadReportPdf_TQC3010_R01(
				getParams(TQC3010_R01Filter),
			);
			// 출력할 검사결과가 없으면 서버가 204 로 알려준다. 오류가 아니라 안내로 처리한다.
			if (response.status === 204) {
				showWarning("출력할 출하검사 결과가 없습니다. 검사결과를 먼저 저장해주세요.");
				return;
			}
			// 진짜 실패는 사유가 text/plain 으로 온다. 텍스트로 풀어 그대로 보여준다.
			const contentType = String(response.headers?.["content-type"] ?? "");
			if (response.status >= 400 || !contentType.includes("application/pdf")) {
				const detail =
					response.data instanceof Blob ? (await response.data.text()).trim() : "";
				showError(detail || "출력 중 오류가 발생했습니다.");
				return;
			}
			const blob = new Blob([response.data], { type: "application/pdf" });
			const fileUrl = URL.createObjectURL(blob);
			window.open(fileUrl, "_blank");
			setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
		} catch (error) {
			const data = (error as { response?: { data?: unknown } })?.response?.data;
			const detail = data instanceof Blob ? (await data.text()).trim() : "";
			showError(detail || "출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC3010_R01Filter, setLoadingSpinner]);

	const initContainerData = useCallback(() => initData(), [initData]);
	return {
		fetchData,
		handleHeaderRowClick,
		handleSaveClick,
		handleDeleteClick,
		handleExcelClick,
		handlePrintClick,
		initContainerData,
	};
};
